import { CostCenter } from "@/interfaces/costCenter";

import { MutationParams } from "@/interfaces/mutationParams";
import axios from "@/lib/axios";
import { useMutation, useQuery } from "@tanstack/vue-query";
import { useToast } from "vue-toastification";
import useCustomMutation from "./useCustomMutation";

interface Params {
  id?: string | number;
}

interface Props {
  params: Params;
  immediate?: boolean;
}

type UpdateParams = Omit<CostCenter, "department" | "departmentId">;

const getCostCenter = async (params: Params): Promise<CostCenter> => {
  const { data } = await axios.get<CostCenter>(
    `/api/cost-centers/${params.id}`,
  );

  return data;
};

const updateCostCenter = async (params: UpdateParams) => {
  const { data } = await axios.put(`/api/cost-centers/${params.id}`, params);

  return data;
};

const deleteCostCenter = async (params: Omit<Params, "companyId">) => {
  const { data } = await axios.delete(`/api/cost-centers/${params.id}`);
  return data;
};

const useCostCenter = (props?: Props) => {
  const { params, immediate = true } = props || {};
  const toast = useToast();

  const { data: costCenter, isLoading } = useQuery(
    ["costCenter", params],
    () =>
      getCostCenter({
        id: params?.id!,
      }),
    {
      enabled: immediate,
      retry: false,
    },
  );

  const updateMutation = useMutation(
    ({ params }: MutationParams<UpdateParams>) => updateCostCenter(params),
    useCustomMutation(toast, "update"),
  );
  const deleteMutation = useMutation(
    ({ params }: MutationParams<Omit<Params, "companyId">>) =>
      deleteCostCenter(params),
    useCustomMutation(toast, "delete"),
  );

  return {
    costCenter,
    isLoading,

    deleteMutation,
    deleteCostCenter: deleteMutation.mutate,
    deleteCostCenterAsync: deleteMutation.mutateAsync,

    updateMutation,
    updateCostCenter: updateMutation.mutate,
    updateCostCenterAsync: updateMutation.mutateAsync,
    updateCostCenterLoading: updateMutation.isLoading,
  };
};

export default useCostCenter;
